"use client";

import { cn } from "@ui/lib";
import { useEffect, useSyncExternalStore } from "react";

type Views = typeof import("./markdown-views");

/*
 * The markdown renderer is a third of the page's JavaScript and the welcome screen renders no
 * markdown, so it loads on demand (SDK-PRELOAD.md 3.2). A module-level store, not React.lazy:
 * lazy suspends once even for a module that is already here, and its plain-text fallback would
 * flash raw `**` over a restored conversation.
 */
let views: Views | null = null;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

/**
 * Starts loading the renderer; resolves when text renders as markdown (or the load failed and
 * plain text stays, to be tried again by the next call).
 */
export function loadMarkdown(): Promise<void> {
	loading ??= import("./markdown-views").then(
		(loaded) => {
			views = loaded;
			for (const listener of listeners) {
				listener();
			}
		},
		() => {
			loading = null;
		},
	);
	return loading;
}

function subscribe(listener: () => void) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

/**
 * Text the assistant wrote. Until the renderer is here it shows as written, which for the
 * one to three plain sentences of a reply is the same text.
 */
export function ShopperMarkdown({
	children,
	streaming = false,
	steps = false,
	className,
}: {
	children: string;
	/** True only while this text is still arriving. */
	streaming?: boolean;
	/** A recipe's method: numbered circles. */
	steps?: boolean;
	className?: string;
}) {
	const loaded = useSyncExternalStore(
		subscribe,
		() => views,
		() => null,
	);
	useEffect(() => {
		void loadMarkdown();
	}, []);
	if (!loaded) {
		return (
			<p dir="auto" className={cn("whitespace-pre-wrap", className)}>
				{children}
			</p>
		);
	}
	return (
		<loaded.Markdown
			streaming={streaming}
			className={className}
			{...(steps ? { components: loaded.RECIPE_STEPS } : {})}
		>
			{children}
		</loaded.Markdown>
	);
}
